import memoize from "fast-memoize";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import CustomFieldType from "@mapmycustomers/shared/types/customField/CustomField";
import { EntitySupportingCustomFields } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel, {
  CategorizedFields,
} from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import { FilterOptionValue } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import ColumnModel, {
  Column,
} from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import i18nService, { Intl } from "@app/config/I18nService";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { displayNameComparator } from "@app/util/sorting";

import isFieldRequiredForIntegration from "../../integrations/isFieldRequiredForIntegration";

import CustomField from "./CustomField";
import { sortFields } from "./fieldModelUtil";

const skipInvisibleFieldsFilter = (field: IField) => !field.isArchived;

export const getEmptyCategorizedFields = (): CategorizedFields => ({
  [FieldCategory.ADDRESS]: [],
  [FieldCategory.COMPANY_ADDRESS_FIELDS]: [],
  [FieldCategory.DEAL_ADDRESS_FIELDS]: [],
  [FieldCategory.DETAILS]: [],
  [FieldCategory.NON_REQUIRED]: [],
  [FieldCategory.PARENT_COMPANY_ADDRESS_FIELDS]: [],
  [FieldCategory.PEOPLE_ADDRESS_FIELDS]: [],
  [FieldCategory.RELATIONSHIPS]: [],
  [FieldCategory.REQUIRED]: [],
});

class FieldModel implements IFieldModel {
  public static readonly NAME_FIELD_NAME = "name";

  private readonly _fields: IField[];
  private _customFields: CustomField[];
  private _schema: SchemaField[];
  private readonly _nameToFieldMap: Map<string, IField> = new Map();
  private readonly _platformNameToFieldMap: Map<string, IField> = new Map();
  private readonly _platformFilterNameToFieldMap: Map<string, IField> = new Map();
  private readonly _filterNameToFieldMap: Map<string, IField> = new Map();
  readonly _integrationNameToFieldMap: Map<string, IField> = new Map();
  private readonly _legacyFieldNames: Record<string, IField["name"]>;
  private _fieldFilters: Array<(field: IField) => boolean>;

  private _fieldsGetter: (customFields: CustomField[], applyFilters?: boolean) => IField[] =
    () => [];
  private _sortedFieldsGetter: (customFields: CustomField[], intl: Intl | undefined) => IField[] =
    () => [];

  /**
   * @param fields fields
   * @param legacyFieldNames names of removed fields that saved settings may still use, mapped to
   * the name of the field that replaced them
   */
  constructor(fields: IField[], legacyFieldNames: Record<string, IField["name"]> = {}) {
    this._fields = fields;
    this._legacyFieldNames = legacyFieldNames;
    this._customFields = [];
    this._schema = [];
    this._fieldFilters = [
      // let's always skip invisible fields in all results
      skipInvisibleFieldsFilter,
    ];
    this._createFieldsGetters();
    this._buildMaps();
  }

  // Rebuilds all four lookup maps from currently-filtered fields. Run on
  // construction and on every mutation of _fields, _customFields, or
  // _fieldFilters so that map lookups don't need to re-apply filters per call.
  private _buildMaps(): void {
    this._nameToFieldMap.clear();
    this._platformNameToFieldMap.clear();
    this._platformFilterNameToFieldMap.clear();
    this._filterNameToFieldMap.clear();
    this._integrationNameToFieldMap.clear();

    const applyFilters = <T extends IField>(fields: T[]): T[] =>
      this._fieldFilters.reduce((result, filter) => result.filter(filter), fields);

    applyFilters(this._fields).forEach((field) => {
      this._nameToFieldMap.set(field.name, field);
      this._platformNameToFieldMap.set(field.platformName, field);
      if (field.hasAnyFeature(FieldFeature.FILTERABLE, FieldFeature.FILTERABLE_ON_MAP)) {
        field.platformFilterNames.forEach((name) => {
          this._platformFilterNameToFieldMap.set(name, field);
        });
      }
      // only original (non-aliased) fields participate in filterName lookup
      if (field.name === field.filterName) {
        this._filterNameToFieldMap.set(field.filterName, field);
      }
      this._integrationNameToFieldMap.set(field.integrationName, field);
    });

    // Fallback: when one of a paired field set is hidden by a visibility filter
    // (e.g. activity PERSON vs PEOPLE under the multiContactActivities add-on),
    // legacy stored layouts still reference the absent field's platformName.
    // Let the surviving field claim those names via its platformFilterNames so
    // getByPlatformName resolves. Primary platformName entries set above win.
    applyFilters(this._fields).forEach((field) => {
      field.platformFilterNames.forEach((name) => {
        if (!this._platformNameToFieldMap.has(name)) {
          this._platformNameToFieldMap.set(name, field);
        }
      });
    });

    // Saved settings may still reference removed fields by their legacy names.
    Object.entries(this._legacyFieldNames).forEach(([legacyName, currentName]) => {
      const field = this._nameToFieldMap.get(currentName);
      if (field) {
        this._nameToFieldMap.set(legacyName, field);
        this._platformNameToFieldMap.set(legacyName, field);
      }
    });

    // Custom fields are dual-keyed (esKey + id) for localStorage back-compat:
    // older saved filters reference the numeric id, newer ones reference name.
    applyFilters(this._customFields).forEach((customField) => {
      const idKey = `${customField.customFieldData.id}`;
      this._nameToFieldMap.set(customField.name, customField);
      this._nameToFieldMap.set(idKey, customField);
      this._platformNameToFieldMap.set(customField.name, customField);
      this._platformNameToFieldMap.set(idKey, customField);
      this._platformFilterNameToFieldMap.set(customField.name, customField);
      this._platformFilterNameToFieldMap.set(idKey, customField);
      this._filterNameToFieldMap.set(customField.filterName, customField);
      this._filterNameToFieldMap.set(idKey, customField);
      this._integrationNameToFieldMap.set(customField.customFieldData.crmPropertyKey, customField);
    });
  }

  private _createFieldsGetters() {
    this._fieldsGetter = memoize(
      (customFields: CustomField[], applyFilters: boolean = true): IField[] => {
        const fields = this._fields.concat(customFields);
        return applyFilters
          ? this._fieldFilters.reduce((result, filter) => result.filter(filter), fields)
          : fields;
      },
      {
        // otherwise it uses monadic strategy since the 2nd argument has a default value
        strategy: memoize.strategies.variadic,
      }
    );
    // intl argument is needed to force re-sort when intl gets initialized
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this._sortedFieldsGetter = memoize((customFields: CustomField[], intl): IField[] =>
      sortFields(this._fieldsGetter(customFields))
    );
  }

  get fields(): IField[] {
    return this._fieldsGetter(this._customFields);
  }

  get allFields(): IField[] {
    return this._fieldsGetter(this._customFields, false);
  }

  get sortedFields() {
    // pass intl to force re-sort when intl gets initialized
    return this._sortedFieldsGetter(this._customFields, i18nService.getIntl());
  }

  get categorizedFields(): CategorizedFields {
    const result = this.sortedFields.reduce<CategorizedFields>((result, field) => {
      if (field.hasFeature(FieldFeature.REQUIRED)) {
        result[FieldCategory.REQUIRED].push(field);
      } else if (field.hasFeature(FieldFeature.RELATIONSHIPS_CATEGORY)) {
        result[FieldCategory.RELATIONSHIPS].push(field);
      } else if (field.hasFeature(FieldFeature.DETAILS)) {
        result[FieldCategory.DETAILS].push(field);
      } else if (field.hasFeature(FieldFeature.PARENT_COMPANY_ADDRESS_FIELD)) {
        result[FieldCategory.PARENT_COMPANY_ADDRESS_FIELDS].push(field);
      } else if (field.hasFeature(FieldFeature.COMPANY_ADDRESS_FIELD)) {
        result[FieldCategory.COMPANY_ADDRESS_FIELDS].push(field);
      } else if (field.hasFeature(FieldFeature.PEOPLE_ADDRESS_FIELD)) {
        result[FieldCategory.PEOPLE_ADDRESS_FIELDS].push(field);
      } else if (field.hasFeature(FieldFeature.DEAL_ADDRESS_FIELD)) {
        result[FieldCategory.DEAL_ADDRESS_FIELDS].push(field);
      } else if (field.hasFeature(FieldFeature.ADDRESS)) {
        result[FieldCategory.ADDRESS].push(field);
      } else {
        result[FieldCategory.NON_REQUIRED].push(field);
      }
      return result;
    }, getEmptyCategorizedFields());
    Object.keys(result).forEach((category) => {
      result[category as FieldCategory] =
        result[category as FieldCategory].sort(displayNameComparator);
    });
    return result;
  }

  getIntegrationCategorizedFields = (service?: IntegrationService): CategorizedFields => {
    const result = this.categorizedFields;
    const requiredAndNonRequiredFields = [
      ...result[FieldCategory.REQUIRED],
      ...result[FieldCategory.NON_REQUIRED],
    ];
    result[FieldCategory.REQUIRED] = [];
    result[FieldCategory.NON_REQUIRED] = [];
    requiredAndNonRequiredFields
      // SALESFORCE and DYNAMICS service don't support funnel field
      .filter(
        (field) =>
          !field.hasFeature(FieldFeature.FUNNEL_FIELD) ||
          (service !== IntegrationService.SALESFORCE && service !== IntegrationService.DYNAMICS)
      )
      .forEach((field) => {
        if (isFieldRequiredForIntegration(field, service)) {
          result[FieldCategory.REQUIRED].push(field);
        } else {
          result[FieldCategory.NON_REQUIRED].push(field);
        }
      });
    return result;
  };

  get categorizedFieldsForDataView(): Record<FieldCategory, IField[]> {
    const categories = this.categorizedFields;
    return Object.keys(categories).reduce(
      (result, categoryKey) => {
        const fields = categories[categoryKey as FieldCategory].filter((field) =>
          field.hasFeature(FieldFeature.DATA_VIEW)
        );
        return {
          ...result,
          ...(fields.length ? { [categoryKey as FieldCategory]: fields } : {}),
        };
      },
      {} as Record<FieldCategory, IField[]>
    );
  }

  getByName(name: string): IField | undefined {
    return this._nameToFieldMap.get(name);
  }

  getByPlatformName(name: string): IField | undefined {
    return this._platformNameToFieldMap.get(name);
  }

  getByPlatformFilterName(name: string): IField | undefined {
    return this._platformFilterNameToFieldMap.get(name);
  }

  getByFilterName(name: string): IField | undefined {
    return this._filterNameToFieldMap.get(name);
  }

  /**
   * Updates field model with given custom fields
   * @param {CustomFieldType[]} fields custom fields as they're returned by the backend
   * @param {[FieldFeature[]]} extraFeatures extra features to add to CustomField instances
   */
  setCustomFields = (fields: CustomFieldType[], extraFeatures?: FieldFeature[]) => {
    this._customFields = fields.map(
      (field) => new CustomField(field, extraFeatures ? { features: extraFeatures } : undefined)
    );
    this._buildMaps();
  };

  setSchema = (schema: SchemaField[]) => {
    this._schema = schema;
    schema.forEach((schemaField) => {
      const field = this._platformNameToFieldMap.get(schemaField.field);
      field?.setSchema(schemaField);
      field?.toggleFeature(FieldFeature.SUPPORTS_UNIQUENESS, schemaField.canBeUnique);
      field?.toggleFeature(FieldFeature.UNIQUE, schemaField.isUnique);
    });
    // re-create fields getters
    this._createFieldsGetters();
  };

  get hasCustomFields(): boolean {
    return !!this._customFields.length;
  }

  doesEntityHaveCustomFieldsWithValues(entity: EntitySupportingCustomFields): boolean {
    return this._customFields.some((field) => field.hasNonEmptyValueFor(entity));
  }

  doesEntityHaveAddress(entity: unknown): boolean {
    return this._fields.some(
      (field) => field.hasFeature(FieldFeature.ADDRESS) && field.hasNonEmptyValueFor(entity)
    );
  }

  getDefaultListViewState(): ViewState {
    const range: RangeModel = { endRow: DEFAULT_PAGE_SIZE, startRow: 0 };
    const filter: FilterModel = { conditions: [], operator: "AND" };
    const sort: SortModel = this.getDefaultSortOrder();

    // by default get fields in the same order they were defined
    // in a field model. Make custom fields hidden.
    const columns: ColumnModel = this.sortedFields
      .filter((field) => !field.hasFeature(FieldFeature.NON_LIST_VIEW))
      .map<Column>((field) => ({
        field,
        pinned: field.hasFeature(FieldFeature.LOCKED) ? "left" : undefined,
        visible: field.hasAnyFeature(FieldFeature.VISIBLE_BY_DEFAULT, FieldFeature.ALWAYS_VISIBLE),
        width: field.hasFeature(FieldFeature.LOCKED) ? 380 : undefined,
      }))
      .sort((a, b) => {
        return (
          (a.field.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.field.displayOrder ?? Number.MAX_SAFE_INTEGER)
        );
      });

    return { columns, filter, range, selectedSavedFilterId: undefined, sort };
  }

  // try to sort by updatedAt descending if such field exists
  // try to sort by name ascending if such field exists
  // otherwise don't sort, use some arbitrary order from platform
  private getDefaultSortOrder(): SortModel {
    const sortModel: SortModel = [];
    const updatedAtField = this.getByName("updatedAt");
    if (updatedAtField) {
      sortModel.push({ field: updatedAtField, order: SortOrder.DESC });
    } else {
      const nameField = this.getByName("name");
      if (nameField) {
        sortModel.push({ field: nameField, order: SortOrder.ASC });
      }
    }
    return sortModel;
  }

  getByIntegrationName(name: string): IField | undefined {
    return this._integrationNameToFieldMap.get(name);
  }

  setFilterOption(option: FilterOption, value: FilterOptionValue, onlyField?: IField) {
    this.fields.forEach((field) => {
      if (!onlyField || (onlyField && onlyField.name === field.name)) {
        field.setFilterOption(option, value);
      }
    });
  }

  addFieldFilter(filter: (field: IField) => boolean): void {
    this._fieldFilters.push(filter);
    this._createFieldsGetters();
    this._buildMaps();
  }

  removeFieldFilter(filter: (field: IField) => boolean): void {
    this._fieldFilters = this._fieldFilters.filter((item) => item !== filter);
    this._createFieldsGetters();
    this._buildMaps();
  }
}

export default FieldModel;
